import React, { useMemo, useState } from "react";
import deptRates from "../data/deptRates2026.json";

/**
 * === CONSTANTES (calcul détaillé) ===
 * Ancien (DMTO standard):
 * - Taxe départementale : taux variable selon département
 * - Taxe communale : 1,20%
 * - Frais de recouvrement : 2,37% de la taxe départementale
 * - CSI : 0,10% (min 15€)
 *
 * Neuf (taux réduit usuel sur TPF) :
 * - Taxe départementale : 0,70%
 * - Pas de taxe communale
 * - Frais de recouvrement : 2,14% de la taxe départementale
 * - CSI : 0,10% (min 15€)
 *
 * Émoluments (barème par tranches, HT) + TVA 20%
 * Débours + formalités : estimations réglables
 */

const COMMUNAL_RATE = 0.012; // 1,20%
const COLLECTION_OLD = 0.0237; // 2,37% de la taxe dept (ancien)
const COLLECTION_NEW = 0.0214; // 2,14% de la taxe dept (neuf taux réduit)
const CSI_RATE = 0.001; // 0,10%
const CSI_MIN = 15;

const EMO_BANDS = [
  { upTo: 6500, rate: 0.0387 },
  { upTo: 17000, rate: 0.01596 },
  { upTo: 60000, rate: 0.01064 },
  { upTo: Infinity, rate: 0.00799 },
];

function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}
function eur(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

function calcEmolumentsHT(base) {
  let remaining = base;
  let prevCap = 0;
  let total = 0;

  for (const band of EMO_BANDS) {
    const cap = band.upTo;
    const tranche = Math.max(0, Math.min(remaining, cap - prevCap));
    total += tranche * band.rate;
    remaining -= tranche;
    prevCap = cap;
    if (remaining <= 0) break;
  }

  return total;
}

function calcCSI(base) {
  return Math.max(CSI_MIN, base * CSI_RATE);
}

function calcTaxesAncien(base, deptRate) {
  const deptTax = base * deptRate;
  const communalTax = base * COMMUNAL_RATE;
  const collectionFees = deptTax * COLLECTION_OLD;
  const csi = calcCSI(base);
  const total = deptTax + communalTax + collectionFees + csi;
  return { deptTax, communalTax, collectionFees, csi, total };
}

function calcTaxesNeuf(base) {
  const deptTax = base * 0.007; // 0,70%
  const communalTax = 0;
  const collectionFees = deptTax * COLLECTION_NEW;
  const csi = calcCSI(base);
  const total = deptTax + collectionFees + csi;
  return { deptTax, communalTax, collectionFees, csi, total };
}

export default function EstimationFraisNotaire() {
  const [price, setPrice] = useState("200000");
  const [type, setType] = useState("ancien"); // ancien | neuf | terrain
  const [deptCode, setDeptCode] = useState("62");

  // frais d’agence inclus dans le prix ?
  const [includeAgency, setIncludeAgency] = useState(false);
  const [agencyFees, setAgencyFees] = useState("0");

  // frais annexes estimatifs
  const [debours, setDebours] = useState("1000");
  const [formalites, setFormalites] = useState("400");

  const computed = useMemo(() => {
    const p = clamp(parseNum(price), 0, 100_000_000);

    const ag = includeAgency ? clamp(parseNum(agencyFees), 0, p) : 0;
    const base = Math.max(0, p - ag);

    const dept = deptRates.find((d) => d.code === deptCode);
    const deptName = dept?.name || "Département";
    const deptRate = Number.isFinite(dept?.deptRate) ? dept.deptRate : 0.045;

    // Taxes
    let taxes;
    if (type === "neuf") taxes = calcTaxesNeuf(base);
    else taxes = calcTaxesAncien(base, deptRate); // ancien & terrain (approx)

    // Notaire (émoluments HT + TVA)
    const emoHT = calcEmolumentsHT(base);
    const emoTVA = emoHT * 0.2;
    const emoTTC = emoHT + emoTVA;

    const deb = clamp(parseNum(debours), 0, 50_000);
    const form = clamp(parseNum(formalites), 0, 50_000);

    const totalFees = taxes.total + emoTTC + deb + form;

    return {
      p,
      ag,
      base,
      deptName,
      deptRate,
      taxes,
      emoHT,
      emoTVA,
      emoTTC,
      deb,
      form,
      totalFees,
    };
  }, [price, type, deptCode, includeAgency, agencyFees, debours, formalites]);

  return (
    <div className="page">
      <div className="hero">
        <h1>Estimation des frais de notaire</h1>
        <p className="muted" style={{ margin: 0 }}>
          Calcul détaillé : département + taxes + émoluments + TVA + CSI +
          débours (estim.).
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Entrées */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          <label className="label">
            Prix d’achat (€)
            <input
              className="input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div className="grid-2">
            <label className="label">
              Type de bien
              <select
                className="select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="ancien">Ancien</option>
                <option value="neuf">Neuf</option>
                <option value="terrain">Terrain</option>
              </select>
            </label>

            <label className="label">
              Département
              <select
                className="select"
                value={deptCode}
                onChange={(e) => setDeptCode(e.target.value)}
              >
                {deptRates.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.code} — {d.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginTop: 8,
            }}
          >
            <input
              type="checkbox"
              checked={includeAgency}
              onChange={(e) => setIncludeAgency(e.target.checked)}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              Le prix inclut des frais d’agence
            </span>
          </label>

          {includeAgency && (
            <label className="label" style={{ marginTop: 10 }}>
              Montant des frais d’agence (€)
              <input
                className="input"
                value={agencyFees}
                onChange={(e) => setAgencyFees(e.target.value)}
                inputMode="decimal"
              />
              <div className="small" style={{ marginTop: 6 }}>
                La base de calcul est estimée sur le prix hors frais d’agence.
              </div>
            </label>
          )}

          <div style={{ height: 12 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Frais annexes (estimations)
          </h3>
          <div className="grid-2">
            <label className="label">
              Débours (€)
              <input
                className="input"
                value={debours}
                onChange={(e) => setDebours(e.target.value)}
                inputMode="decimal"
              />
            </label>
            <label className="label">
              Formalités (€)
              <input
                className="input"
                value={formalites}
                onChange={(e) => setFormalites(e.target.value)}
                inputMode="decimal"
              />
            </label>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn btnSecondary"
              onClick={() => {
                setPrice("200000");
                setType("ancien");
                setDeptCode("62");
                setIncludeAgency(false);
                setAgencyFees("0");
                setDebours("1000");
                setFormalites("400");
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Estimation indicative : certains frais/abattements/cas particuliers
            peuvent modifier le résultat.
          </div>
        </div>

        {/* Résultats */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          <ResultRow
            label="Base de calcul (hors frais agence)"
            value={eur(computed.base)}
          />
          <ResultRow
            label={`Taux départemental (${computed.deptName})`}
            value={`${(computed.deptRate * 100).toFixed(2)} %`}
          />

          <div style={{ height: 10 }} />
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: 16,
              letterSpacing: ".2px",
            }}
          >
            Taxes
          </h3>

          <ResultRow
            label="Taxe départementale"
            value={eur(computed.taxes.deptTax)}
          />
          {computed.taxes.communalTax > 0 && (
            <ResultRow
              label="Taxe communale (1,20%)"
              value={eur(computed.taxes.communalTax)}
            />
          )}
          <ResultRow
            label="Frais de recouvrement"
            value={eur(computed.taxes.collectionFees)}
          />
          <ResultRow
            label="CSI (0,10% min 15€)"
            value={eur(computed.taxes.csi)}
          />
          <ResultRow label="Total taxes" value={eur(computed.taxes.total)} />

          <div style={{ height: 10 }} />
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: 16,
              letterSpacing: ".2px",
            }}
          >
            Notaire
          </h3>

          <ResultRow label="Émoluments (HT)" value={eur(computed.emoHT)} />
          <ResultRow
            label="TVA sur émoluments (20%)"
            value={eur(computed.emoTVA)}
          />
          <ResultRow label="Émoluments (TTC)" value={eur(computed.emoTTC)} />

          <div style={{ height: 10 }} />
          <ResultRow label="Débours (estim.)" value={eur(computed.deb)} />
          <ResultRow label="Formalités (estim.)" value={eur(computed.form)} />

          <div style={{ height: 12 }} />
          <div
            className="card"
            style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
          >
            <div
              className="resultRow"
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span className="muted">Total frais estimés</span>
              <strong>{eur(computed.totalFees)}</strong>
            </div>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Conseil : on peut ajouter une option “biens mobiliers” (meubles)
            pour affiner la base taxable.
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value }) {
  return (
    <div
      className="resultRow"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
